/**
 * NoticeCalculator — PUBLIC lead-magnet page at /tools/notice-calculator.
 *
 * The second calculator after /tools/rent-calculator. "How much notice do
 * I have to give" is the other question Irish landlords (and tenants)
 * search with a live problem in front of them; this page answers it, then
 * converts on the thing a calculator can't do: produce the notice itself.
 *
 * Same constraints as RentCalculator.js, whose structure this copies:
 *  - NO AUTH. Renders for a logged-out cold click. All legal logic lives
 *    in ../../utils/noticePeriods.js, which has zero imports. ExitIntent-
 *    Feedback only touches Firestore inside its submit handler.
 *  - Same dark marketing look, nav and CTA treatment.
 *  - SEO via usePageMeta (title/description/canonical/OG + JSON-LD). The
 *    FAQ JSON-LD is built from the same array that renders on the page.
 *
 * The rules and their sources are documented in noticePeriods.js. This
 * file renders them; it does not decide them.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Container, Grid, Paper, Stack, TextField, MenuItem,
  Alert, Divider, Link as MuiLink, ToggleButton, ToggleButtonGroup,
  ThemeProvider, createTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventIcon from '@mui/icons-material/Event';
import { useNavigate } from 'react-router-dom';
import ExitIntentFeedback from '../../components/ExitIntentFeedback';
import usePageMeta from '../../utils/pageMeta';
import { trackEvent } from '../../utils/analytics';
import {
  calculateNotice,
  todayISO,
  formatDateIE,
  parseISO,
  REFORM_DATE,
  LANDLORD_REASONS,
  TENANT_REASONS,
  LANDLORD_NOTICE_TABLE,
  TENANT_NOTICE_TABLE,
} from '../../utils/noticePeriods';

const RED = '#E63946';
// White text on #E63946 is 4.17:1 (fails AA); #C8303C is 5.32:1. RED is
// only an accent on the dark background; RED_SOLID carries white text.
const RED_SOLID = '#C8303C';
const BG = '#0F1117';
const BG_ALT = '#0A0C11';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: RED_SOLID },
    background: { default: BG, paper: '#171A22' },
    text: { primary: '#FFFFFF', secondary: 'rgba(255,255,255,0.65)' },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
});

const PAGE_TITLE = 'Notice of Termination Calculator (Ireland, 2026) | NestHub';
const PAGE_DESC =
  'Free Irish notice of termination calculator for landlords and tenants: the RTB notice '
  + 'period, earliest termination date and what a valid notice must include.';
const CANONICAL = 'https://nesthub.ie/tools/notice-calculator';

const FAQS = [
  {
    q: 'How much notice does a landlord have to give to end a tenancy in Ireland?',
    a: 'It depends on how long the tenancy has lasted on the day the notice is served: 90 days '
      + 'for less than 6 months (and no more than 120), 152 days for 6 months to under a year, 180 '
      + 'days for 1 to under 7 years, 196 days for 7 to under 8 years, and 224 days for 8 years or '
      + 'more. Shorter periods apply only where the tenant is at fault: 28 days for rent arrears or '
      + 'another breach after the required warning, and 7 days for serious anti-social behaviour.',
  },
  {
    q: 'How much notice does a tenant have to give?',
    a: '28 days for a tenancy of less than 6 months, 35 days for 6 months to under a year, 42 days '
      + 'for 1 to under 2 years, 56 days for 2 to under 4 years, 84 days for 4 to under 8 years, and '
      + '112 days for 8 years or more. If the landlord is in breach and has not fixed it after written '
      + 'notice, a tenant can give 28 days; if the landlord’s behaviour poses an imminent danger, 7 days.',
  },
  {
    q: 'Did the March 2026 rental reforms change the notice periods?',
    a: 'No — the notice periods are the same. What changed, for tenancies created on or after 1 March '
      + '2026, is the reasons a landlord may rely on. After 6 months such a tenancy becomes a 6-year '
      + '“Tenancy of Minimum Duration”. A company or a landlord with 4 or more tenancies can then end '
      + 'it only for tenant breach or because the property no longer suits the household; a landlord '
      + 'with 1 to 3 tenancies can also move in or house close family, and can sell (except in '
      + 'hardship cases), refurbish or change use only at the end of a 6-year cycle. Tenancies that '
      + 'began before 1 March 2026 keep the older rules.',
  },
  {
    q: 'How do I count the days in a notice period?',
    a: 'Day 1 is the day after the notice is served, so 90 days’ notice served on 1 June runs from 2 '
      + 'June and the earliest termination date is 30 August. The tenant has the whole 24 hours of the '
      + 'termination date to leave. The length of the tenancy is measured on the date of service, so '
      + 'serving a day before an anniversary can mean a shorter period — and a day after, a longer one.',
  },
  {
    q: 'Does a Notice of Termination have to be sent to the RTB?',
    a: 'A landlord’s notice does. Since 14 September 2026 a copy must reach the RTB within 7 days of '
      + 'serving the tenant; before that it had to be sent on the same day. If the copy is missing or '
      + 'late the notice is invalid. The RTB also advises serving the tenant by email, by hand or by '
      + 'leaving it at the address rather than by post. A tenant’s notice does not need to go to the RTB.',
  },
  {
    q: 'Do these rules apply if I rent a room in my own home?',
    a: 'No. A lodger in the owner’s own home is a licensee, not a tenant, and the Residential Tenancies '
      + 'Act does not apply: there is no statutory notice period, no RTB registration and no RTB '
      + 'dispute process. The law expects “reasonable notice” — 28 days is the usual convention — or '
      + 'whatever the licence agreement says.',
  },
];

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const PAGE_JSONLD = [
  FAQ_JSONLD,
  {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Notice of Termination Calculator (Ireland)',
    url: CANONICAL,
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web',
    browserRequirements: 'Requires JavaScript',
    description: PAGE_DESC,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
    provider: { '@type': 'Organization', name: 'NestHub', url: 'https://nesthub.ie/' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nesthub.ie/' },
      { '@type': 'ListItem', position: 2, name: 'Notice calculator', item: CANONICAL },
    ],
  },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.05)' },
  // 44px touch target (WCAG 2.5.8).
  '& .MuiOutlinedInput-input': { paddingTop: '13px', paddingBottom: '13px' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#fff', borderWidth: 2 },
  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)' },
};

const toggleSx = {
  width: '100%',
  '& .MuiToggleButton-root': {
    flex: 1, minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.75)', borderColor: 'rgba(255,255,255,0.15)',
  },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
    backgroundColor: RED_SOLID, color: '#fff',
  },
  '& .MuiToggleButton-root:focus-visible': { outline: '2px solid #fff', outlineOffset: -2 },
};

const labelSx = { fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)', mb: 0.75 };

const NO_FAULT = ['sale', 'own_use', 'refurbishment', 'change_of_use'];

export default function NoticeCalculator() {
  const navigate = useNavigate();

  const [giver, setGiver] = useState('landlord');
  const [agreementType, setAgreementType] = useState('tenancy');
  const [tenancyStartDate, setTenancyStartDate] = useState('');
  const [servedDate, setServedDate] = useState(todayISO());
  const [landlordReason, setLandlordReason] = useState('sale');
  const [tenantReason, setTenantReason] = useState('standard');
  const [landlordSize, setLandlordSize] = useState('');
  const [arrearsWarningReceived, setArrearsWarningReceived] = useState('');
  const [touched, setTouched] = useState(false);

  usePageMeta({
    title: PAGE_TITLE,
    description: PAGE_DESC,
    canonical: '/tools/notice-calculator',
    jsonLd: PAGE_JSONLD,
  });

  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = BG;
    return () => { document.body.style.backgroundColor = prevBg; };
  }, []);

  const reason = giver === 'landlord' ? landlordReason : tenantReason;

  const result = useMemo(() => {
    if (!tenancyStartDate || !servedDate) return null;
    return calculateNotice({
      giver, tenancyStartDate, servedDate, agreementType, reason, landlordSize, arrearsWarningReceived,
    });
  }, [giver, tenancyStartDate, servedDate, agreementType, reason, landlordSize, arrearsWarningReceived]);

  // Only ask "how many tenancies?" when the answer changes the result:
  // a landlord, a tenancy created from 1 March 2026, a no-fault ground.
  const startDate = parseISO(tenancyStartDate);
  const showSize = giver === 'landlord' && agreementType === 'tenancy'
    && startDate && startDate >= parseISO(REFORM_DATE) && NO_FAULT.includes(landlordReason);
  const showArrears = giver === 'landlord' && agreementType === 'tenancy' && landlordReason === 'arrears';

  // One GA event per distinct result, not per keystroke.
  const resultKey = result && result.ok
    ? `${giver}|${agreementType}|${result.reason}|${result.noticeDays}|${result.earliestTerminationDate}`
    : null;
  useEffect(() => {
    if (!resultKey || !result || !result.ok) return;
    trackEvent('notice_calculator_result', {
      giver,
      agreement_type: agreementType,
      reason: result.reason || 'licence',
      notice_days: result.noticeDays,
      duration_months: result.duration ? result.duration.months : null,
      post_reform: result.isPostReform,
      landlord_size: landlordSize || 'unknown',
      has_blocking_warning: result.warnings.some((w) => w.level === 'error'),
    });
  }, [resultKey]);

  const goSignup = useCallback((placement) => {
    trackEvent('notice_calculator_cta_click', { placement });
    navigate('/signup?utm_source=notice-calculator');
  }, [navigate]);

  const goRentCalc = (e) => {
    e.preventDefault();
    trackEvent('notice_calculator_crosslink', { to: 'rent-calculator' });
    navigate('/tools/rent-calculator');
  };

  const showErrors = touched && result && !result.ok;
  const blocked = result && result.ok && result.warnings.some((w) => w.level === 'error');

  const dates = [];
  if (result && result.ok) {
    dates.push({ label: 'Notice served', value: formatDateIE(servedDate), note: 'The day the notice is emailed, handed over or left at the address' });
    dates.push({ label: 'Day 1 of the notice period', value: formatDateIE(result.noticeStartDate), note: 'The notice period starts the day after service' });
    dates.push({ label: 'Earliest termination date', value: formatDateIE(result.earliestTerminationDate), note: 'The tenant has the whole 24 hours of this day to leave', strong: true });
    if (result.latestTerminationDate) {
      dates.push({ label: 'Latest termination date', value: formatDateIE(result.latestTerminationDate), note: 'For a tenancy under 6 months the notice can be no longer than 120 days' });
    }
    if (result.rtbCopy.required && result.rtbCopy.deadline) {
      dates.push({ label: 'Copy to the RTB by', value: formatDateIE(result.rtbCopy.deadline), note: result.rtbCopy.rule === 'same_day' ? 'Same day as the tenant (notices served before 14 September 2026)' : 'Within 7 days of serving the tenant, or the notice is invalid', warn: true });
    }
    if (result.disputeDeadline) {
      dates.push({ label: `${giver === 'landlord' ? 'Tenant' : 'Landlord'} can dispute validity until`, value: formatDateIE(result.disputeDeadline), note: `${result.disputeDays} days from receiving the notice` });
    }
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ backgroundColor: BG, color: '#fff', minHeight: '100vh' }}>
        {/* ── Nav (matches RentCalculator / LandlordsIreland) ─────── */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'rgba(15,17,23,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
              <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: RED_SOLID, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>N</Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.125rem' }}>NestHub</Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button onClick={() => navigate('/login')} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 600, display: { xs: 'none', sm: 'inline-flex' } }}>Sign in</Button>
              <Button variant="contained" onClick={() => goSignup('nav')} sx={{ backgroundColor: RED_SOLID, '&:hover': { backgroundColor: '#A8232E' }, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', minHeight: 44 }}>
                Start free trial
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* ── Hero ───────────────────────────────────────────── */}
        <Container maxWidth="md" sx={{ textAlign: 'center', pt: { xs: 5, md: 8 }, pb: { xs: 3, md: 4 } }}>
          <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.75rem' }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, mb: 2 }}>
            Notice of Termination Calculator <Box component="span" sx={{ color: RED }}>(Ireland, 2026)</Box>
          </Typography>
          <Typography sx={{ fontSize: { xs: '0.9375rem', md: '1.0625rem' }, color: 'rgba(255,255,255,0.65)', maxWidth: 660, mx: 'auto' }}>
            For landlords and tenants: the notice period the law requires, the earliest date the
            tenancy can end, and everything a valid notice must contain — including the rules for
            tenancies created after 1 March 2026. Free, no signup.
          </Typography>
        </Container>

        {/* ── Calculator ─────────────────────────────────────── */}
        <Container maxWidth="lg" sx={{ pb: { xs: 5, md: 7 } }}>
          <Grid container spacing={3}>
            {/* Inputs */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 2.5, md: 3 }, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
                <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.0625rem', mb: 2.5 }}>
                  Your notice
                </Typography>

                <Stack spacing={2.5}>
                  <Box>
                    <Typography id="giver-label" sx={labelSx}>Who is giving notice?</Typography>
                    <ToggleButtonGroup
                      exclusive value={giver} aria-labelledby="giver-label" sx={toggleSx}
                      onChange={(e, v) => { if (v) { setGiver(v); setTouched(true); } }}
                    >
                      <ToggleButton value="landlord">Landlord</ToggleButton>
                      <ToggleButton value="tenant">Tenant</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box>
                    <Typography id="agreement-label" sx={labelSx}>Type of agreement</Typography>
                    <ToggleButtonGroup
                      exclusive value={agreementType} aria-labelledby="agreement-label" sx={toggleSx}
                      onChange={(e, v) => { if (v) { setAgreementType(v); setTouched(true); } }}
                    >
                      <ToggleButton value="tenancy">Tenancy</ToggleButton>
                      <ToggleButton value="licence">Licence (room in owner’s home)</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <TextField
                    label={agreementType === 'licence' ? 'Licence start date' : 'Tenancy start date'}
                    type="date" fullWidth size="small" sx={fieldSx}
                    value={tenancyStartDate}
                    onChange={(e) => { setTenancyStartDate(e.target.value); setTouched(true); }}
                    InputLabelProps={{ shrink: true }}
                    helperText="The date the tenancy began — not the latest lease renewal"
                  />

                  <TextField
                    label="Date the notice will be served"
                    type="date" fullWidth size="small" sx={fieldSx}
                    value={servedDate}
                    onChange={(e) => { setServedDate(e.target.value); setTouched(true); }}
                    InputLabelProps={{ shrink: true }}
                    helperText="Defaults to today. Tenancy length is measured on this date."
                  />

                  {agreementType === 'tenancy' && (
                    giver === 'landlord' ? (
                      <TextField
                        select label="Reason for ending the tenancy" fullWidth size="small" sx={fieldSx}
                        value={landlordReason}
                        onChange={(e) => { setLandlordReason(e.target.value); setTouched(true); }}
                      >
                        {LANDLORD_REASONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                      </TextField>
                    ) : (
                      <TextField
                        select label="Reason for leaving" fullWidth size="small" sx={fieldSx}
                        value={tenantReason}
                        onChange={(e) => { setTenantReason(e.target.value); setTouched(true); }}
                      >
                        {TENANT_REASONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                      </TextField>
                    )
                  )}

                  {showSize && (
                    <Box>
                      <Typography id="size-label" sx={labelSx}>How many tenancies do you have?</Typography>
                      <ToggleButtonGroup
                        exclusive value={landlordSize} aria-labelledby="size-label" sx={toggleSx}
                        onChange={(e, v) => { if (v) setLandlordSize(v); }}
                      >
                        <ToggleButton value="small">1–3, not a company</ToggleButton>
                        <ToggleButton value="large">4 or more, or a company</ToggleButton>
                      </ToggleButtonGroup>
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mt: 0.75 }}>
                        Tenancies created from 1 March 2026 have different permitted reasons for small and larger landlords.
                      </Typography>
                    </Box>
                  )}

                  {showArrears && (
                    <TextField
                      label="Date the arrears warning was received (optional)"
                      type="date" fullWidth size="small" sx={fieldSx}
                      value={arrearsWarningReceived}
                      onChange={(e) => setArrearsWarningReceived(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText="The later of the tenant or the RTB receiving it — checks the 28-day payment window"
                    />
                  )}
                </Stack>

                <Box sx={{ mt: 2.5, p: 2, backgroundColor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px' }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <InfoOutlinedIcon sx={{ color: '#60A5FA', fontSize: 18, mt: 0.1 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>New from 14 September 2026</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
                    A landlord now has 7 days after serving the tenant to send the RTB its copy (it used
                    to be the same day), and the RTB advises serving by email or by hand rather than by post.
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Results */}
            <Grid item xs={12} md={6}>
              <Box aria-live="polite">
                {showErrors && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    <Stack spacing={0.5}>
                      {result.errors.map((e) => <Typography key={e} sx={{ fontSize: '0.875rem' }}>{e}</Typography>)}
                    </Stack>
                  </Alert>
                )}

                {result && result.ok ? (
                  <Stack spacing={2}>
                    {/* Headline */}
                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: blocked ? 'rgba(251,191,36,0.06)' : 'rgba(16,185,129,0.08)', border: `1px solid ${blocked ? 'rgba(251,191,36,0.4)' : 'rgba(16,185,129,0.35)'}` }}>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: blocked ? '#FBBF24' : '#6EE7B7', letterSpacing: '0.1em', mb: 1 }}>
                        EARLIEST VALID TERMINATION DATE
                      </Typography>
                      <Typography sx={{ fontSize: { xs: '1.875rem', md: '2.375rem' }, fontWeight: 800, color: blocked ? '#FBBF24' : '#6EE7B7', lineHeight: 1.1 }}>
                        {formatDateIE(result.earliestTerminationDate)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', mt: 0.75 }}>
                        if served on {formatDateIE(servedDate)}
                        {blocked && ' — but see the warning below: this reason is not available to you'}
                      </Typography>
                      <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                      <Grid container spacing={2}>
                        <Grid item xs={5}>
                          <Typography sx={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Notice period
                          </Typography>
                          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>
                            {result.noticeDays} days{result.isStatutory ? '' : ' (usual)'}
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography sx={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {agreementType === 'licence' ? 'Licence length' : 'Tenancy length at service'}
                          </Typography>
                          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>{result.durationText}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Guidance-not-advice, next to the number */}
                    <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, px: 0.5 }}>
                      <strong>Guidance, not legal advice — check the RTB.</strong>{' '}
                      Confirm against the{' '}
                      <MuiLink href="https://rtb.ie/renting/ending-a-tenancy/notice-of-termination-guides/" target="_blank" rel="noopener noreferrer" sx={{ color: '#fff' }}>
                        RTB’s Notice of Termination guide
                      </MuiLink>{' '}
                      before you serve anything. A notice with the wrong date or a missing step is invalid.
                    </Typography>

                    {/* Why this number */}
                    <Paper sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1 }}>The rule that applies</Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>
                        {result.noticeBasis}
                      </Typography>
                    </Paper>

                    {/* Warnings */}
                    {result.warnings.map((w) => (
                      <Alert key={w.text} severity={w.level} sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.875rem', lineHeight: 1.6 } }}>
                        {w.text}
                      </Alert>
                    ))}

                    {/* Key dates */}
                    <Paper sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                        <EventIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.6)' }} />
                        <Typography component="h3" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Your dates</Typography>
                      </Box>
                      <Stack spacing={1.5}>
                        {dates.map((d) => (
                          <Box key={d.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                            {d.warn
                              ? <WarningAmberIcon sx={{ color: '#FBBF24', fontSize: 17, mt: 0.3 }} />
                              : <CheckCircleIcon sx={{ color: '#10B981', fontSize: 17, mt: 0.3 }} />}
                            <Box>
                              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                                {d.label}: <Box component="span" sx={{ color: d.warn ? '#FBBF24' : '#6EE7B7', fontWeight: d.strong ? 800 : 600 }}>{d.value}</Box>
                              </Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>{d.note}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                      {!result.rtbCopy.required && (
                        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mt: 1.5 }}>
                          RTB copy: {result.rtbCopy.text}
                        </Typography>
                      )}
                      {result.rtbCopy.required && result.rtbCopy.rule === 'check' && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#FBBF24', mt: 1.5 }}>{result.rtbCopy.text}</Typography>
                      )}
                    </Paper>

                    {/* Checklist */}
                    <Paper sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <GavelIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.6)' }} />
                        <Typography component="h3" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                          {agreementType === 'licence' ? 'Ending a licence properly' : 'What the notice must contain'}
                        </Typography>
                      </Box>
                      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                        {result.checklist.map((item) => (
                          <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                            <CheckCircleIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16, mt: 0.25 }} />
                            <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>{item}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>

                    {/* Conversion CTA */}
                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.35)', textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.125rem', mb: 1 }}>
                        Now generate the notice itself
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', mb: 2.5, lineHeight: 1.6 }}>
                        NestHub builds the Notice of Termination from your tenancy record with the right
                        period and dates, reminds you of the RTB copy deadline, and keeps the proof of service.
                      </Typography>
                      <Button
                        variant="contained" size="large" fullWidth endIcon={<ArrowForwardIcon />}
                        onClick={() => goSignup('result')}
                        sx={{ backgroundColor: RED_SOLID, '&:hover': { backgroundColor: '#A8232E' }, textTransform: 'none', fontWeight: 700, py: 1.4 }}
                      >
                        Generate a compliant Notice of Termination in NestHub — free 14-day trial
                      </Button>
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', mt: 1.5 }}>
                        No credit card required · cancel anytime
                      </Typography>
                    </Paper>
                  </Stack>
                ) : (
                  !showErrors && (
                    <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.15)' }}>
                      <GavelIcon sx={{ fontSize: 44, color: 'rgba(255,255,255,0.25)', mb: 1.5 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                        Enter the tenancy start date
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
                        Your notice period, earliest termination date, RTB deadline and the
                        notice checklist will appear here.
                      </Typography>
                    </Paper>
                  )
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Reference tables */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {[
              { title: 'Landlord notice periods', rows: LANDLORD_NOTICE_TABLE },
              { title: 'Tenant notice periods', rows: TENANT_NOTICE_TABLE },
            ].map((t) => (
              <Grid item xs={12} md={6} key={t.title}>
                <Paper sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography component="h2" sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1.25 }}>{t.title}</Typography>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead>
                      <tr>
                        <Box component="th" scope="col" sx={{ textAlign: 'left', color: 'rgba(255,255,255,0.6)', fontWeight: 600, pb: 0.75 }}>Tenancy length</Box>
                        <Box component="th" scope="col" sx={{ textAlign: 'right', color: 'rgba(255,255,255,0.6)', fontWeight: 600, pb: 0.75 }}>Notice</Box>
                      </tr>
                    </thead>
                    <tbody>
                      {t.rows.map((r) => (
                        <Box component="tr" key={r.label} sx={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <Box component="td" sx={{ py: 0.75, color: 'rgba(255,255,255,0.8)' }}>{r.label}</Box>
                          <Box component="td" sx={{ py: 0.75, textAlign: 'right', fontWeight: 700 }}>{r.days} days</Box>
                        </Box>
                      ))}
                    </tbody>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Legal footing */}
          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
            <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.7 }}>
              Based on the Residential Tenancies Act 2004 as amended — including the Residential
              Tenancies (Miscellaneous Provisions) Act 2026 (from 1 March 2026) and the 14 September 2026
              changes (rules checked 27 September 2026). It is guidance, not legal advice, and cannot
              know every fact about your tenancy. Check the{' '}
              <MuiLink href="https://rtb.ie/renting/ending-a-tenancy/notice-of-termination-guides/" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                RTB Notice of Termination guides
              </MuiLink>{' '}
              and use the RTB’s template for your reason. Tenants can get free advice from Threshold
              (1800 454 454). Raising the rent instead? Try our{' '}
              <MuiLink href="/tools/rent-calculator" onClick={goRentCalc} sx={{ fontWeight: 600 }}>
                RTB rent increase calculator
              </MuiLink>.
            </Typography>
          </Alert>
        </Container>

        {/* ── FAQ ────────────────────────────────────────────── */}
        <Box sx={{ py: { xs: 5, md: 7 }, backgroundColor: BG_ALT, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <Container maxWidth="md">
            <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, textAlign: 'center', mb: 4 }}>
              Notice of termination in Ireland: common questions
            </Typography>
            <Stack spacing={2}>
              {FAQS.map((f) => (
                <Paper key={f.q} sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                  <Typography component="h3" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.9375rem' }}>{f.q}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7 }}>{f.a}</Typography>
                </Paper>
              ))}
            </Stack>
          </Container>
        </Box>

        {/* ── Final CTA ──────────────────────────────────────── */}
        <Box sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
          <Container maxWidth="md">
            <Typography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, mb: 1.5 }}>
              Never serve an invalid notice
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', mb: 3.5, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
              NestHub keeps every tenancy’s start date, works out the notice period for you, generates
              the notice and tracks the RTB deadline — for every property you let.
            </Typography>
            <Button
              variant="contained" size="large" endIcon={<ArrowForwardIcon />}
              onClick={() => goSignup('footer')}
              sx={{ backgroundColor: RED_SOLID, '&:hover': { backgroundColor: '#A8232E' }, textTransform: 'none', fontWeight: 700, px: 5, py: 1.5, fontSize: '1rem' }}
            >
              Start your free 14-day trial
            </Button>
            <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', mt: 2 }}>
              Built for Irish landlords · 2026 rules built in · cancel anytime
            </Typography>
          </Container>
        </Box>

        {/* Cold ad traffic bounces here too — same once-per-browser gate. */}
        <ExitIntentFeedback />
      </Box>
    </ThemeProvider>
  );
}
